/**
 * Assassin Workshop — Compatibility AW lightweight bootstrap
 * Lot 8 / v1.2.0
 * Owns only the initial SEO/safety/tracking layer and loads the heavy engine on demand.
 */
(() => {
  'use strict';

  const bootstrapScript = document.currentScript;
  const roots = Array.from(document.querySelectorAll('[data-aw-compatibility-search]'));
  if (!roots.length || window.AWCompatibilityBootstrap?.version) return;

  const normalize = (value = '') => String(value)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  const ensureLightAnalytics = () => {
    if (window.AWAnalytics?.track) return;
    const themeRole = String(window.Shopify?.theme?.role || '').toLowerCase();
    const preview = Boolean(
      window.Shopify?.designMode ||
      (themeRole && themeRole !== 'main') ||
      /(?:\?|&)preview_theme_id=/.test(location.search) ||
      /(?:\?|&)previewPath=/.test(location.search)
    );
    const eventName = (value = '') => String(value).trim().toLowerCase().normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    const safe = (value) => {
      if (value === null || value === undefined) return undefined;
      if (typeof value === 'boolean' || typeof value === 'number') return value;
      if (typeof value === 'string') return value.slice(0, 180);
      if (Array.isArray(value)) return value.slice(0, 12).map(safe).filter((item) => item !== undefined);
      return undefined;
    };
    const track = (name, payload = {}) => {
      const event = eventName(name);
      if (!event) return;
      const extra = Object.entries(payload).reduce((result, [key, value]) => {
        const cleanKey = eventName(key).slice(0, 60);
        const cleanValue = safe(value);
        if (cleanKey && cleanValue !== undefined) result[cleanKey] = cleanValue;
        return result;
      }, {});
      const data = {
        schema_version: 1,
        path: location.pathname.slice(0, 180),
        locale: document.documentElement.lang || '',
        currency: window.Shopify?.currency?.active || '',
        preview_mode: preview,
        ...extra
      };
      const fullName = `assassin_workshop:${event}`;
      window.dispatchEvent(new CustomEvent('aw:analytics', { detail: { event: fullName, payload: data, suppressed: preview } }));
      if (preview) return;
      const publish = (attempt = 0) => {
        try {
          if (window.Shopify?.analytics?.publish) {
            window.Shopify.analytics.publish(fullName, data);
            return;
          }
        } catch (_) { return; }
        if (attempt < 8) setTimeout(() => publish(attempt + 1), 250);
      };
      publish();
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: `aw_${event}`, ...data });
      } catch (_) {}
    };
    window.AWAnalytics = { version: 'compatibility-lite-1.0.0', track, preview };
  };

  ensureLightAnalytics();

  const normalizeWording = (root) => {
    const directory = root.querySelector('.aw-compatibility-search__directory');
    if (directory && !directory.id && !document.getElementById('repertoire-aw')) directory.id = 'repertoire-aw';
    const trust = root.querySelector('.aw-compatibility-search__trust strong');
    if (trust?.textContent.trim() === 'Données catalogue vérifiées') trust.textContent = 'Données catalogue référencées';
    const status = root.querySelector('.aw-compatibility-search__identity-status small');
    if (status?.textContent.trim() === 'Les produits sont filtrés selon les données AW.') status.textContent = 'Les produits sont classés selon les données AW disponibles.';
    const summary = root.querySelector('[id^="AwCompatibilitySummary-"]');
    if (summary?.textContent.trim() === 'Compatibilité par catégorie') summary.textContent = 'Correspondances par catégorie';
    const total = root.querySelector('[data-aw-total-count]');
    if (total) {
      const match = total.textContent.trim().match(/^(\d+)\s+produits?\s+compatibles?$/i);
      if (match) total.textContent = `${match[1]} résultat${match[1] === '1' ? '' : 's'} pour cette plateforme`;
    }
    const catalog = root.querySelector('.aw-compatibility-search__catalog');
    if (catalog) {
      const eyebrow = catalog.querySelector('.aw-compatibility-search__section-heading p');
      if (eyebrow?.textContent.trim() === 'SÉLECTION COMPATIBLE') eyebrow.textContent = 'RÉSULTATS PAR PLATEFORME';
      const heading = catalog.querySelector('[id^="AwCompatibilityCatalog-"]');
      if (heading?.textContent.trim() === 'Produits compatibles') heading.textContent = 'Produits correspondant à votre plateforme';
      const loading = catalog.querySelector('[data-aw-catalog-loading]');
      if (loading?.textContent.trim() === 'Analyse des produits compatibles…') loading.textContent = 'Analyse des produits correspondant à la plateforme…';
      const empty = catalog.querySelector('[data-aw-empty]');
      if (empty?.textContent.trim() === 'Aucun produit compatible n’est encore renseigné pour cette sélection.') empty.textContent = 'Aucun produit correspondant n’est encore renseigné pour cette sélection.';
    }
  };

  const installWordingGuard = (root) => {
    if (root.dataset.awCompatibilityWordingGuard === 'true') return;
    root.dataset.awCompatibilityWordingGuard = 'true';
    normalizeWording(root);
    const observer = new MutationObserver(() => normalizeWording(root));
    observer.observe(root, { childList: true, subtree: true, characterData: true });
  };

  const prepareDirectory = (root) => {
    normalizeWording(root);
    if (root.dataset.seoPagesEnabled !== 'true') return;
    const sources = Array.from(root.querySelectorAll('[data-aw-model-data]'));
    const cards = Array.from(root.querySelectorAll('[data-aw-directory-card]'));
    cards.forEach((card, index) => {
      const button = card.querySelector('[data-aw-model-button]');
      const source = sources[index];
      const url = source?.dataset.seoUrl;
      if (!button || !url) return;
      const link = document.createElement('a');
      link.href = url;
      link.className = button.className;
      link.dataset.awP1Track = 'compatibility_model_page';
      link.innerHTML = 'Voir les compatibilités <span aria-hidden="true">→</span>';
      button.replaceWith(link);
    });
  };

  roots.forEach((root) => {
    installWordingGuard(root);
    prepareDirectory(root);
  });

  document.addEventListener('click', (event) => {
    const tracked = event.target?.closest?.('[data-aw-p1-track]');
    if (!tracked) return;
    window.AWAnalytics?.track?.(tracked.dataset.awP1Track || 'p1_click', {
      cluster: tracked.dataset.awP1Cluster || '',
      href: tracked.getAttribute('href') || '',
      label: (tracked.textContent || '').trim().slice(0, 120)
    });
  }, true);

  let loaded = false;
  let loading = null;
  let attributionLoading = null;
  let replaying = false;
  let failed = false;

  const siblingSrc = (filename) => {
    try {
      const url = new URL(bootstrapScript?.src || '', window.location.href);
      url.pathname = url.pathname.replace(/aw-compatibility-search\.js$/, filename);
      return url.href;
    } catch (_) {
      return '';
    }
  };

  const coreSrc = siblingSrc('aw-compatibility-search-core.js');

  const degrade = (root) => {
    if (!root) return;
    root.dataset.awCompatibilityDegraded = 'true';
    const feedback = root.querySelector('[data-aw-search-feedback]');
    if (feedback) {
      feedback.textContent = 'Compatibility AW n’a pas pu démarrer. Rechargez la page ou utilisez le répertoire de modèles et ses pages Compatibility ci-dessous.';
      feedback.setAttribute('role', 'alert');
    }
  };
  const fail = (error) => {
    failed = true;
    loading = null;
    roots.forEach(degrade);
    return error instanceof Error ? error : new Error('Compatibility core unavailable');
  };

  const ensureAttribution = () => {
    if (window.AWAttribution?.version) return Promise.resolve();
    if (attributionLoading) return attributionLoading;
    const src = siblingSrc('aw-attribution-v2.js');
    if (!src) return Promise.resolve();
    attributionLoading = new Promise((resolve) => {
      const node = document.createElement('script');
      node.src = src;
      node.async = true;
      node.dataset.awLazyAttribution = 'compatibility';
      node.addEventListener('load', resolve, { once: true });
      node.addEventListener('error', resolve, { once: true });
      document.head.appendChild(node);
    });
    return attributionLoading;
  };

  const activateDirectoryDeepLink = () => {
    const platform = normalize(new URL(location.href).searchParams.get('aw_platform') || '');
    if (!platform) return;
    roots.forEach((root) => {
      const target = Array.from(root.querySelectorAll('[data-aw-directory-filter]'))
        .find((button) => normalize(button.dataset.awDirectoryFilter) === platform);
      if (target && !target.classList.contains('is-active')) target.click();
    });
    if (location.hash === '#repertoire-aw') {
      setTimeout(() => document.getElementById('repertoire-aw')?.scrollIntoView({ block: 'start' }), 80);
    }
    window.AWAnalytics?.track?.('compatibility_directory_deeplink', { platform });
  };

  const load = () => {
    if (loaded) return Promise.resolve();
    if (failed) return Promise.reject(new Error('Compatibility core unavailable'));
    if (loading) return loading;
    if (!coreSrc) return Promise.reject(fail(new Error('Compatibility core URL unavailable')));

    ensureAttribution().catch(() => {});
    loading = new Promise((resolve, reject) => {
      const node = document.createElement('script');
      node.src = coreSrc;
      node.async = true;
      node.dataset.awCompatibilityCore = 'true';
      node.addEventListener('load', () => {
        loaded = true;
        roots.forEach((root) => {
          root.dataset.awCompatibilityCoreLoaded = 'true';
          prepareDirectory(root);
          normalizeWording(root);
        });
        activateDirectoryDeepLink();
        resolve();
      }, { once: true });
      node.addEventListener('error', () => {
        reject(fail(new Error('Compatibility core failed to load')));
      }, { once: true });
      document.head.appendChild(node);
    });
    return loading;
  };

  const warm = () => { load().catch(() => {}); };

  roots.forEach((root) => {
    if (root.dataset.awCompatibilityBootstrap === 'true') return;
    root.dataset.awCompatibilityBootstrap = 'true';
    root.addEventListener('pointerover', warm, { once: true, passive: true });
    root.addEventListener('pointerdown', warm, { once: true, passive: true });
    root.addEventListener('touchstart', warm, { once: true, passive: true });
    root.addEventListener('focusin', warm, { once: true });

    root.addEventListener('input', (event) => {
      if (failed) { degrade(root); return; }
      if (loaded || replaying) return;
      const target = event.target;
      load().then(() => {
        if (!target?.dispatchEvent) return;
        replaying = true;
        target.dispatchEvent(new Event('input', { bubbles: true }));
        replaying = false;
      }).catch(() => { degrade(root); });
    }, { capture: true, once: true });

    root.addEventListener('click', (event) => {
      if (failed) { degrade(root); return; }
      if (loaded || replaying || event.defaultPrevented) return;
      const target = event.target?.closest?.('button,[role="button"]');
      if (!target || !root.contains(target)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      load().then(() => {
        replaying = true;
        target.click();
        replaying = false;
      }).catch(() => { degrade(root); });
    }, true);

    root.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (failed) { event.preventDefault(); degrade(root); return; }
      if (loaded || replaying) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      load().then(() => {
        replaying = true;
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        replaying = false;
      }).catch(() => { degrade(root); });
    }, true);
  });

  const params = new URLSearchParams(location.search);
  const hasDeepLink = Array.from(params.keys()).some((key) => key.startsWith('aw_'));
  const hasDirectoryAnchor = /^#repertoire-aw(?:$|[-_])/i.test(location.hash);
  const isModelPage = roots.some((root) => root.dataset.modelPage === 'true');
  const hasSavedCompatibility = roots.some((root) => {
    const keys = [
      root.dataset.platformKey || 'aw_selected_platform',
      root.dataset.manufacturerKey || 'aw_selected_manufacturer',
      root.dataset.typeKey || 'aw_selected_replica_type'
    ];
    try { return keys.some((key) => localStorage.getItem(key)); } catch (_) { return false; }
  });

  window.AWCompatibilityBootstrap = {
    version: '1.2.1',
    load,
    get loaded() { return loaded; },
    get failed() { return failed; }
  };

  if (hasDeepLink || hasDirectoryAnchor || isModelPage || hasSavedCompatibility || window.Shopify?.designMode) warm();
})();
